import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import {
  profileDetails,
  profileUpdate,
  changePassword,
} from "../../../services/ProfileService";
import useMutation from "../../../hooks/useMutation";
import { setProfile } from "../../../redux/slice/profileSlice";
import ImageUploader from "../../../components/form/ImageUploader";
import useForm from "../../../hooks/useForm";
import Input from "../../../components/form/Input";
import { formValidate } from "../../../helpers/formValidate";
import PhoneNumberFormat from "../../../components/form/PhoneNumberFormat";
import Button from "../../../components/form/Button";
import Textarea from "../../../components/form/Textarea";
function ProfileComponent() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state?.profile?.data);
  const [image, setImage] = useState();
  const [phone, setPhone] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const validation = {
    firstName: { required: true, message: "Please enter your first name!" },
    lastName: { required: true, message: "Please enter your last name!" },
  };

  const { values, handleChange, handleSubmit, errors, setFieldsValue } =
    useForm({
      validationRules: validation,
    });

  useEffect(() => {
    const fetchProfile = async () => {
      const res = await profileDetails();
      if (res?.status === 200) {
        const data = res?.data;
        setFieldsValue({
          firstName: data?.firstName || "",
          lastName: data?.lastName || "",
          email: data?.email || "",
          about: data?.about || "",
        });
        setPhone(data?.phone || "");
        setImage(data?.image?.url || "");
        dispatch(setProfile(data));
      }
    };
    fetchProfile();
  }, [dispatch]);

  const { mutate: profileMutate, isPending: profileLoading } = useMutation({
    mutationFn: async (val) => {
      return await profileUpdate({
        firstName: val.firstName,
        lastName: val.lastName,
        about: val.about,
        image: image || "null",
        phone: phone || "null", // Update phone number if present
      });
    },
    onSuccess: async (res) => {
      if (res?.status === 200) {
        toast.success(res?.message || "Profile updated successfully!");
        dispatch(setProfile({ ...user, ...(res?.data || {}) }));
      } else {
        toast.error(res?.data?.message || "Something went wrong");
      }
    },
  });

  const { mutate: changePasswordMutate, isPending: passwordLoading } =
    useMutation({
      mutationFn: async (params) => {
        return await changePassword({
          currentPassword: params.currentPassword,
          newPassword: params.newPassword,
          confirmPassword: params.confirmPassword,
        });
      },
      onSuccess: (res) => {
        if (res?.status === 200) {
          toast.success("Password changed successfully!");
          setCurrentPassword("");
          setNewPassword("");
          setConfirmPassword("");
        } else {
          toast.error(res?.message || "Error changing password");
        }
      },
    });

  return (
    <div className="grid grid-cols-12 gap-6 p-6">
      {/* Profile Picture */}
      <div className="col-span-12 lg:col-span-4 flex flex-col items-center lg:border-r border-slate-200">
        <ImageUploader
          isMultiple={false}
          isCrop={false}
          removeIcon={true}
          image={image}
          setImage={setImage}
          buttonLabel={image ? "Change Profile" : "Upload Profile"}
          buttonLabelClasses="!text-xs"
          labelClasses="text-lg font-bold"
          buttonIcon="fa-upload text-xs"
          buttonClasses="!px-1 !py-2 bg-blue-500 text-white rounded-lg hover:bg-red-600 mx-auto !h-auto !w-[94%]"
          inputClasses="w-32 h-32 hover:bg-slate-200 rounded-full border-2 border-gray-300 overflow-hidden"
        />
      </div>

      {/* Profile Info */}
      <div className="col-span-12 lg:col-span-8 flex flex-col">
        <form
          onSubmit={(e) => handleSubmit(e, profileMutate)}
          className="space-y-6"
        >
          <div className="grid md:grid-cols-2 gap-6">
            <Input
              label="First Name"
              inputType="text"
              value={values.firstName}
              inputPlaceholder="Enter first name"
              inputName="firstName"
              onChange={handleChange}
              required
              {...formValidate(errors, "firstName")}
            />
            <Input
              label="Last Name"
              inputType="text"
              value={values.lastName}
              inputPlaceholder="Enter last name"
              inputName="lastName"
              onChange={handleChange}
              required
              {...formValidate(errors, "lastName")}
            />
            {/* Email Field - visually disabled */}
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={values.email}
                disabled
                placeholder="Enter email"
                className="w-full rounded-lg border border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed px-4 py-2"
              />
            </div>

            <PhoneNumberFormat
              label="Phone"
              value={phone}
              isInputGroup={true}
              inputGroupIcon={"fa-solid fa-phone"}
              inputGroupPosition={"left"}
              setPhone={setPhone}
              inputPlaceholder={"(xxx) xxx-xxxx"}
              charLimit={12}
              required={true}
            />
          </div>

          <div className="w-full mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              About
            </label>
            <Textarea
              inputName="about"
              inputValue={values.about}
              onChange={handleChange}
              inputPlaceholder="Write something about yourself"
              rows={6}
              className="w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 px-4 py-3 resize-none shadow-sm"
            />
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              buttonType="submit"
              buttonIcon="fa-light fa-floppy-disk"
              buttonLabel="Save"
              loading={profileLoading}
              isDisable={profileLoading}
              buttonClasses="!py-2 !px-4 rounded-lg text-white !bg-chestnut-rose hover:!bg-red-600"
            />
          </div>
        </form>

        {/* Change Password Form */}
        <div className="mt-8">
          <h3 className="text-lg font-bold text-gray-700">Change Password</h3>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (newPassword !== confirmPassword) {
                toast.error("Passwords do not match!");
                return;
              }
              changePasswordMutate({
                currentPassword,
                newPassword,
                confirmPassword,
              });
            }}
            className="space-y-4"
          >
            <div className="grid md:grid-cols-2 gap-6">
              <Input
                label="Current Password"
                inputType="password"
                value={currentPassword}
                inputPlaceholder="Enter current password"
                inputName="currentPassword"
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
              />
              <Input
                label="New Password"
                inputType="password"
                value={newPassword}
                inputPlaceholder="Enter new password"
                inputName="newPassword"
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
              <Input
                label="Confirm New Password"
                inputType="password"
                value={confirmPassword}
                inputPlaceholder="Confirm new password"
                inputName="confirmPassword"
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
            <div className="flex justify-end">
              <Button
                buttonType="submit"
                buttonIcon="fa-solid fa-key"
                buttonLabel="Change Password"
                loading={passwordLoading}
                isDisable={passwordLoading}
                buttonClasses="!py-2 !px-4 rounded-lg text-white !bg-chestnut-rose hover:!bg-red-600"
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ProfileComponent;
